import {
  type NarrowFieldsByValueType,
  TidalFormState,
  FormFieldDescriptor,
  TidalFormBridgeOptions,
  useTidalFormBridge,
} from '@workwave-tidal/core/form'

import {
  FormHelperText,
  Checkbox,
  FormControlLabel,
  FormControlLabelProps,
  CheckboxProps as BaseCheckboxProps,
  FormControl,
} from '@mui/material'

import { BulkFieldValue } from './types'

export type BulkCheckboxValue = BulkFieldValue<boolean>
const defaultComponentValue = {
  status: 'exact',
  value: false,
}

export type BulkCheckboxProps<
  TargetField extends NarrowFieldsByValueType<AcceptedFormValue, State, FormFieldDescriptor<State>>,
  State extends TidalFormState,
  AcceptedFormValue = BulkCheckboxValue,
> = {
  formControlLabelProps?: Partial<FormControlLabelProps>
  checkboxProps?: Partial<BaseCheckboxProps>
} & TidalFormBridgeOptions<BulkCheckboxValue, TargetField, State, AcceptedFormValue>

export function BulkCheckbox<
  TargetField extends NarrowFieldsByValueType<AcceptedFormValue, State, FormFieldDescriptor<State>>,
  State extends TidalFormState,
  AcceptedFormValue = BulkCheckboxValue,
>(props: BulkCheckboxProps<TargetField, State, AcceptedFormValue>) {
  const {
    checkboxProps = {},
    formControlLabelProps = {},
    initialValue = defaultComponentValue as FormFieldDescriptor<State>[TargetField]['value'],
    validateOn = 'change',
    ...bridgeOptions
  } = props

  const {
    config: { fullWidth, label, name, testId = 'checkbox-root' },
    fieldInfo: {
      errorOrHelperText,
      field: { visible, required },
      fieldDisabled,
      fieldHasError,
      onBlur,
      onChange,
      onFocus,
      showHelperText,
      value,
    },
  } = useTidalFormBridge<BulkCheckboxValue, TargetField, State, AcceptedFormValue>({
    ...bridgeOptions,
    validateOn,
    initialValue,
  })

  if (!visible) return null

  const checked = value.status === 'exact' ? value.value : false
  const indeterminate = value.status === 'mixed'

  const handleOnChange = (_: unknown, checked: boolean) => {
    onChange({ status: 'exact', value: indeterminate ? true : checked })
  }

  return (
    <FormControl
      error={fieldHasError}
      component="fieldset"
      variant="standard"
      fullWidth={fullWidth}
      data-testid={testId}
      required={required}
      data-tidal-form-field-name={bridgeOptions.name}
      sx={{
        '&.tidal-form-devtool-highlight': theme => ({
          outline: `4px solid var(--tidal-devtools-highlight-color, ${theme.vars.palette.error.dark})`,
        }),
      }}
    >
      <FormControlLabel
        name={name}
        control={
          <Checkbox
            onFocus={onFocus}
            onBlur={onBlur}
            onChange={handleOnChange}
            checked={checked}
            indeterminate={indeterminate}
          />
        }
        label={label}
        disabled={fieldDisabled}
      />

      {showHelperText && <FormHelperText error={fieldHasError}>{errorOrHelperText}</FormHelperText>}
    </FormControl>
  )
}
